// src/pages/ChangePasswordPage.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button,
  InputAdornment, IconButton, LinearProgress, Alert, Collapse, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { FaEye, FaEyeSlash, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';

const hasUpper = (s) => /[A-Z]/.test(s);
const hasLower = (s) => /[a-z]/.test(s);
const hasNumber = (s) => /\d/.test(s);

const strengthCalc = (pw) => {
  let score = 0;
  if (!pw) return 0;
  if (pw.length >= 8) score += 25;
  if (hasUpper(pw)) score += 25;
  if (hasLower(pw)) score += 25;
  if (hasNumber(pw)) score += 25;
  return score;
};

export default function ChangePasswordPage() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNew, setConfirmNew] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [openReLogin, setOpenReLogin] = useState(false);

  const strength = useMemo(() => strengthCalc(newPassword), [newPassword]);
  const policy = useMemo(() => ({
    minLen: newPassword.length >= 8,
    upper: hasUpper(newPassword),
    lower: hasLower(newPassword),
    number: hasNumber(newPassword),
  }), [newPassword]);

  const policyOK = policy.minLen && policy.upper && policy.lower && policy.number;
  const canSubmit = !loading && !!oldPassword && !!newPassword && !!confirmNew && policyOK && newPassword === confirmNew;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOkMsg('');
    setErrMsg('');

    if (!canSubmit) {
      if (!oldPassword || !newPassword || !confirmNew) {
        setErrMsg('Completa todos los campos.');
      } else if (!policyOK) {
        setErrMsg('La nueva contraseña no cumple la política.');
      } else if (newPassword !== confirmNew) {
        setErrMsg('Las nuevas contraseñas no coinciden.');
      }
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/users/me/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_new_password: confirmNew
      });
      setOkMsg('¡Contraseña actualizada correctamente!');
      setOldPassword('');
      setNewPassword('');
      setConfirmNew('');
      // Mostrar diálogo sugiriendo re-login
      setOpenReLogin(true);
    } catch (error) {
      const data = error?.response?.data;
      if (Array.isArray(data?.detail)) {
        const msg = data.detail.map(e => {
          const path = Array.isArray(e.loc) ? e.loc.join('.') : e.loc;
          return `${path}: ${e.msg}`;
        }).join(' | ');
        setErrMsg(msg);
      } else {
        const detail = data?.detail;
        setErrMsg(typeof detail === 'string' ? detail : 'No se pudo cambiar la contraseña.');
      }
    } finally {
      setLoading(false);
    }
  };

  const forceLogout = () => {
    // Limpia el token y fuerza recarga para que App reevalúe auth
    localStorage.removeItem('token');
    localStorage.removeItem('nombre');
    window.location.href = '/login';
  };

  const StrengthLabel = () => (
    <Typography variant="caption">
      Fortaleza: {strength <= 25 ? 'Débil' : strength <= 50 ? 'Media' : strength <= 75 ? 'Buena' : 'Fuerte'}
    </Typography>
  );

  const PolicyItem = ({ ok, text }) => (
    <Stack direction="row" spacing={1} alignItems="center">
      {ok ? <FaCheckCircle size={14} /> : <FaRegCircle size={14} />}
      <Typography variant="caption">{text}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
      <Paper sx={{ p: 3, width: '100%', maxWidth: 520 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Cambiar contraseña
        </Typography>

        <Collapse in={!!okMsg} unmountOnExit>
          <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>
        </Collapse>
        <Collapse in={!!errMsg} unmountOnExit>
          <Alert severity="error" sx={{ mb: 2 }}>{errMsg}</Alert>
        </Collapse>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Contraseña actual"
            type={showOld ? 'text' : 'password'}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowOld(x => !x)} edge="end" aria-label="Mostrar/ocultar contraseña actual">
                    {showOld ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="Nueva contraseña"
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            helperText="Debe tener mínimo 8 caracteres, incluir mayúscula, minúscula y número."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(x => !x)} edge="end" aria-label="Mostrar/ocultar nueva contraseña">
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ mt: 1, mb: 1.5 }}>
            <LinearProgress variant="determinate" value={strength} />
            <StrengthLabel />
            <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
              <PolicyItem ok={policy.minLen} text="Mínimo 8 caracteres" />
              <PolicyItem ok={policy.upper}  text="Al menos 1 mayúscula (A–Z)" />
              <PolicyItem ok={policy.lower}  text="Al menos 1 minúscula (a–z)" />
              <PolicyItem ok={policy.number} text="Al menos 1 número (0–9)" />
            </Stack>
          </Box>

          <TextField
            label="Confirmar nueva contraseña"
            type={showConfirm ? 'text' : 'password'}
            value={confirmNew}
            onChange={(e) => setConfirmNew(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm(x => !x)} edge="end" aria-label="Mostrar/ocultar confirmación">
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            {loading ? 'Guardando…' : 'Cambiar contraseña'}
          </Button>
        </Box>
      </Paper>

      {/* Diálogo de re-login */}
      <Dialog open={openReLogin} onClose={() => setOpenReLogin(false)}>
        <DialogTitle>Vuelve a iniciar sesión</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tu contraseña se actualizó correctamente. Por seguridad, te recomendamos cerrar sesión y volver a ingresar con la nueva contraseña.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReLogin(false)}>Más tarde</Button>
          <Button variant="contained" color="primary" onClick={forceLogout}>
            Cerrar sesión ahora
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
